import React from 'react';
import { Row, Col } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSLabel, NSSelect,
} from 'bricks';
import { format, parseISO } from 'date-fns';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './ProFormaPermanentDebt.scss';
import RenderIf from 'components/RenderIf/RenderIf';
import { IPatchOperatingInfoData } from 'views/ProFormaTable/types';
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { INTEREST_OPTIONS } from './constants';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import DebtServiceTable from './DebtServiceTable/DebtServiceTable';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import BaseTotalsTable from '../BaseTotalsTable/BaseTotalsTable';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';

export interface IProFormaPermanentDebtProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaPermanentDebt = ({
    operationBeginIndex, baseCashFlowColumns,
}: IProFormaPermanentDebtProps) => {
    const {
        operatingInfo, handleUpdateProFormaOperations, showCents, periodCadence,
    } = useProFormaSocketContext();

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('permanent-debt-table', ['income-table', 'expenses-table']);

    const updateOperationsKeys = (keyValues: Partial<IPatchOperatingInfoData>) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, keyValues);
        }
    };

    let cashFlowAfterFinancingPeriods = operatingInfo?.cashFlowAfterFinancing?.cashFlow || [];
    if (periodCadence === Periods.ANNUALLY) cashFlowAfterFinancingPeriods = operatingInfo?.cashFlowAfterFinancing?.yearlyCashFlow || [];
    const cashFlowAfterFinancingValues = cashFlowAfterFinancingPeriods.map(period => period.value);

    return (
        <div className="ProFormaPermanentDebt">
            <Row className="mx-n1 pb-3 px-3">
                <Col className="px-1 pb-2" sm={12} md={6}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap">Interest only?</NSLabel>
                        <NSSelect
                            options={INTEREST_OPTIONS}
                            name="isInterestOnly"
                            value={INTEREST_OPTIONS.find(option => operatingInfo.isInterestOnly === option.value)}
                            onChange={option => updateOperationsKeys({ isInterestOnly: option.value })}
                            isClearable={false}
                        />
                    </div>

                    <RenderIf isTrue={!operatingInfo.isInterestOnly}>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap">Amortization</NSLabel>
                            <NSInputNumberStepper
                                name="amortizationInYears"
                                value={operatingInfo.amortizationInYears || null}
                                onChange={(value: number) => updateOperationsKeys({ amortizationInYears: value })}
                                min={0}
                                step={1}
                                customSuffix="years"
                                inputGroupClassName="ProFormaPermanentDebt__input-width"
                            />
                        </div>
                    </RenderIf>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap">APR</NSLabel>
                        <NSInputNumberStepper
                            name="annualPercentageRate"
                            value={operatingInfo.annualPercentageRate || null}
                            onChange={(value: number) => updateOperationsKeys({ annualPercentageRate: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            percentage
                            inputGroupClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap">Fees & closing costs</NSLabel>
                        <NSInputNumberStepper
                            name="feesAndClosingCostsPercentage"
                            value={operatingInfo.feesAndClosingCostsPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({ feesAndClosingCostsPercentage: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            percentage
                            inputGroupClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>
                </Col>
                <Col className="px-1" sm={12} md={6}>
                    <Row className="mx-n1 pt-2">
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Loan funding"
                                value={`${operatingInfo?.loanFundingDate
                                    ? `${format(parseISO(operatingInfo?.loanFundingDate), 'MMM yyyy')} (Month ${operatingInfo.loanFundingMonth})` : ''}`}
                                showInProgress={!operatingInfo?.loanFundingDate}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Loan payoff"
                                value={`${operatingInfo.loanPayoffDate
                                    ? `${format(parseISO(operatingInfo.loanPayoffDate), 'MMM yyyy')} (Month ${operatingInfo.loanPayoffMonth})` : null}`}
                                showInProgress={!operatingInfo.loanPayoffDate}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Debt service coverage ratio"
                                value={`${numeralFormatterCurrency(operatingInfo.debtServiceCoverageRatio || 0, '0,0.00')}x`}
                                showInProgress={!operatingInfo.debtServiceCoverageRatio}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Fees & closing costs"
                                value={numeralFormatterCurrency(Math.abs(operatingInfo.feesAndClosingCostsAmount || 0), showCents ? '$0,0.00' : '$0,0')}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12}>
                            <MetricOutputCard
                                label="Cash-on-cash on return"
                                value={`${percentFormatter(
                                    (operatingInfo.cashOnCashReturn || 0) * 100,
                                )} (Month ${operatingInfo.cashOnCashReturnMonth})`}
                                showInProgress={!operatingInfo.cashOnCashReturn}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div
                className={classNames(['pb-3 pl-3', {
                    'pr-3': !operationBeginIndex,
                }])}
            >
                <div
                    className="ProFormaPermanentDebt__table-wrapper scrollable-container"
                    ref={createRefHandler(tableRef)}
                    onScroll={() => handleTableScroll('table')}
                >
                    <DebtServiceTable
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                    <BaseTotalsTable
                        tableName="Cash flow after financing"
                        totalAmount={operatingInfo?.cashFlowAfterFinancing?.totalAnnualCashFlow || 0}
                        cashFlowValues={cashFlowAfterFinancingValues}
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProFormaPermanentDebt;
